import type {
  ConfirmedAnswers,
  RenewalData,
  SectionDescriptor,
  StepDescriptor,
  StepId,
  StepperStageDescriptor,
} from "./route-table.ts";
import { isSectionConfirmed } from "./route-table.ts";
import type { SectionStateEntry, StepperStageEntry } from "./types.ts";

/**
 * The single shared pure computation of section state (ADR-20260918-dd9e).
 * resume.ts, section-state.ts and the multi-section step route component
 * all consume this SAME computation — there is exactly one place "which
 * sections are open, locked, applicable, confirmed" is decided. No import
 * of the router, no import of any component, no I/O.
 *
 * This module contains no step path literal and no section id: every id
 * it reads comes from the section/step table passed in, and it never
 * imports route-table.ts itself. See routing/README.md's documented
 * heuristic.
 *
 * Algorithm, in one pass over steps in order, then sections within each
 * step in order:
 *
 * - A step is "reached" once every step before it (in step order) is
 *   fully settled (every one of its sections is either confirmed or
 *   inapplicable). The first step is always reached. Steps hosting no
 *   sections at all (declaration, received) never need this module's
 *   attention — resume.ts targets them directly once no section is open.
 * - Every section in an unreached step is "locked", and its isApplicable
 *   is never invoked (AC-6, AC-7: this is exactly how the exposures step
 *   stays untouched while change-check is unconfirmed).
 * - Within a reached step, sections are walked in order with a single
 *   "turn" cursor. Applicability is checked for EVERY section regardless
 *   of turn position (AC-7): an inapplicable section is always "inapplicable"
 *   and never occupies or is counted toward the turn, whether the turn is
 *   still open or has already closed. Among applicable sections, while
 *   the turn is open: a confirmed one is settled (state "confirmed", turn
 *   stays open); the first unconfirmed one takes the turn (state "open",
 *   turn closes). Once the turn is closed, every remaining applicable
 *   section in that step is "unlocked" (reached step, not yet its turn).
 */
/**
 * The per-step turn-cursor loop, extracted from `computeSectionStates`
 * (ADR-20260921-a2x4, resolving the escalated typescript:S3776 finding by
 * splitting out this function's own branching rather than by suppressing
 * it) so the outer function's job — deciding whether a step is reached at
 * all — stays separate from this one's job — walking a single reached
 * step's sections with the "turn" cursor documented on
 * `computeSectionStates` above. Behaviour is unchanged: this is the exact
 * body that previously ran inline when `stepsReachedSoFar` was true.
 *
 * Returns the entries for this step's sections and whether the step ended
 * up fully settled (the turn never closed — every section in it is either
 * confirmed or inapplicable), which is what the caller needs to decide if
 * the next step is reached.
 */
function computeReachedStepEntries(
  step: StepDescriptor,
  sectionsForStep: readonly SectionDescriptor[],
  confirmedAnswers: ConfirmedAnswers,
  renewalData: RenewalData,
): { entries: SectionStateEntry[]; stepSettled: boolean } {
  const entries: SectionStateEntry[] = [];
  let turnOpen = true;

  for (const section of sectionsForStep) {
    // Applicability is evaluated for every section regardless of turn
    // position (AC-7): a policy-excluded section sitting after the open
    // section must be reported "inapplicable", not "unlocked" — otherwise
    // it would render as a visible locked accordion item and be counted
    // in the step's progress denominator.
    if (!section.isApplicable(renewalData, confirmedAnswers)) {
      entries.push({
        sectionId: section.id,
        hostStepId: step.id,
        order: section.order,
        state: "inapplicable",
      });
      continue;
    }

    // Confirmed status, like applicability above, is a fact about the
    // section's own saved state, not about its position relative to the
    // turn cursor (AC-2, FIND-18): a section confirmed in an earlier
    // visit stays "confirmed" — and keeps rendering its saved values —
    // even if a later-added applicable section now sits before it and
    // holds the turn. This must be checked before `!turnOpen` or a
    // confirmed section sitting after the open one would fall into the
    // "unlocked" branch below and be reported locked with its saved
    // values hidden.
    if (isSectionConfirmed(confirmedAnswers, section.id)) {
      entries.push({
        sectionId: section.id,
        hostStepId: step.id,
        order: section.order,
        state: "confirmed",
      });
      continue;
    }

    if (!turnOpen) {
      entries.push({
        sectionId: section.id,
        hostStepId: step.id,
        order: section.order,
        state: "unlocked",
      });
      continue;
    }

    entries.push({
      sectionId: section.id,
      hostStepId: step.id,
      order: section.order,
      state: "open",
    });
    turnOpen = false;
  }

  // The step is fully settled only if the turn never closed — every
  // section in it ended up confirmed or inapplicable.
  return { entries, stepSettled: turnOpen };
}

export function computeSectionStates(
  sectionTable: readonly SectionDescriptor[],
  stepTable: readonly StepDescriptor[],
  confirmedAnswers: ConfirmedAnswers,
  renewalData: RenewalData,
): readonly SectionStateEntry[] {
  const stepsInOrder = [...stepTable].sort((a, b) => a.order - b.order);
  const entries: SectionStateEntry[] = [];
  let stepsReachedSoFar = true;

  for (const step of stepsInOrder) {
    const sectionsForStep = sectionTable
      .filter((section) => section.hostStepId === step.id)
      .sort((a, b) => a.order - b.order);

    if (sectionsForStep.length === 0) {
      // A single-purpose step (no hosted sections) never affects the
      // reached/settled chain for later steps.
      continue;
    }

    if (!stepsReachedSoFar) {
      for (const section of sectionsForStep) {
        entries.push({
          sectionId: section.id,
          hostStepId: step.id,
          order: section.order,
          state: "locked",
        });
      }
      continue;
    }

    const { entries: stepEntries, stepSettled } = computeReachedStepEntries(
      step,
      sectionsForStep,
      confirmedAnswers,
      renewalData,
    );
    entries.push(...stepEntries);
    stepsReachedSoFar = stepSettled;
  }

  return entries;
}

/** True once every section hosted by `stepId` (per `sectionStates`) is
 * either "confirmed" or "inapplicable" -- the same settlement rule
 * `computeReachedStepEntries` above already uses to decide whether a step
 * is fully settled (AC-6/AC-7: a step whose sections are ALL inapplicable,
 * e.g. exposures under change-check="no", counts as settled). A step with
 * no entries in `sectionStates` at all hosts no section in the table
 * (declaration, received) and has no settlement signal here -- its
 * caller-supplied `isHostlessStepSettled` answers instead. */
function isStepSettled(
  stepId: StepId,
  sectionStates: readonly SectionStateEntry[],
  isHostlessStepSettled: (stepId: StepId) => boolean,
): boolean {
  const entriesForStep = sectionStates.filter((entry) => entry.hostStepId === stepId);
  if (entriesForStep.length === 0) {
    return isHostlessStepSettled(stepId);
  }
  return entriesForStep.every((entry) => entry.state === "confirmed" || entry.state === "inapplicable");
}

/**
 * Maps the five-route settlement state onto the three stepper stages
 * (ADR-20261001-rz8p, TG-17), alongside `computeSectionStates` above.
 * Contains no step id or section id literal of its own: every id it
 * reads comes from `stageTable` (route-table.ts's `STEPPER_STAGE_TABLE`,
 * the one place the stage-to-step grouping is declared) and from
 * `sectionStates` (this module's own `computeSectionStates` output).
 *
 * - "Confirm details" is done only once both the confirm-details and
 *   change-check steps are settled (`STEPPER_STAGE_TABLE`'s own
 *   `stepIds` for that stage names both).
 * - "Review exposures" is done once every applicable exposure section is
 *   settled, OR the whole step is not applicable (change-check="no") --
 *   both cases are exactly `isStepSettled`'s "confirmed or inapplicable"
 *   rule above, requiring no separate branch here.
 * - "Declaration" hosts no section in `SECTION_TABLE` at all, so its
 *   settlement cannot be read from `sectionStates` -- no port in this
 *   feature yet models "has the declaration been submitted" (the same
 *   gap App.tsx's own declaration-session-state comment documents).
 *   `isHostlessStepSettled` is that explicit, externally supplied signal.
 *   Callers with no real signal yet must pass `() => false` -- never
 *   guess `true`.
 *
 * Stages are walked in order. Exactly one is "current": the first not
 * yet "done". Every stage before it is "done"; every stage after it is
 * "todo". If every stage is done, none is "current".
 *
 * OBJ-127/FIND-187: that "first not done is current" rule only applies
 * once the customer has actually started -- the AC-1 not-started
 * scenario (product-spec.md:35, TG-17's prototype parity criterion)
 * requires every stage to read "to do" with NONE marked "current" when
 * nothing has been confirmed yet. "Started" is read from the same two
 * signals this function already takes, mirroring `isStepSettled`'s own
 * rule for which signal governs which step: any `sectionStates` entry in
 * state "confirmed" (an `inapplicable` section is not progress -- it is
 * a section the customer never had to act on), OR -- for a step that
 * hosts no section at all, i.e. has zero `sectionStates` entries, the
 * same condition `isStepSettled` uses to decide whether to consult the
 * caller at all -- `isHostlessStepSettled` returning true for that step.
 * A step that does host sections is never asked of `isHostlessStepSettled`
 * here, matching `isStepSettled`'s own conditional use of it below. No
 * new parameter: the discriminator is derived, not supplied, so callers
 * cannot guess it.
 */
export function computeStepperStages(
  stageTable: readonly StepperStageDescriptor[],
  sectionStates: readonly SectionStateEntry[],
  isHostlessStepSettled: (stepId: StepId) => boolean,
): readonly StepperStageEntry[] {
  const stagesInOrder = [...stageTable].sort((a, b) => a.order - b.order);
  const entries: StepperStageEntry[] = [];

  const hasAnyProgress =
    sectionStates.some((entry) => entry.state === "confirmed") ||
    stagesInOrder.some((stage) =>
      stage.stepIds.some(
        (stepId) =>
          sectionStates.every((entry) => entry.hostStepId !== stepId) && isHostlessStepSettled(stepId),
      ),
    );

  if (!hasAnyProgress) {
    return stagesInOrder.map((stage) => ({ stageId: stage.id, order: stage.order, state: "todo" }));
  }

  let currentAssigned = false;

  for (const stage of stagesInOrder) {
    const stageSettled = stage.stepIds.every((stepId) =>
      isStepSettled(stepId, sectionStates, isHostlessStepSettled),
    );

    if (stageSettled) {
      entries.push({ stageId: stage.id, order: stage.order, state: "done" });
      continue;
    }

    if (!currentAssigned) {
      entries.push({ stageId: stage.id, order: stage.order, state: "current" });
      currentAssigned = true;
    } else {
      entries.push({ stageId: stage.id, order: stage.order, state: "todo" });
    }
  }

  return entries;
}
